export interface MissingPhoto {
  type: string;
  description: string;
  impact: string;
}

export interface Platform {
  id: string;
  name: string;
}

export interface ContextConfig {
  id: string;
  name: string;
  emoji: string;
  description: string;
  traits: string[];
  platforms: Platform[];
  resultLabel: string;
  missingPhotos: MissingPhoto[];
}

export const CONTEXTS: ContextConfig[] = [
  {
    id: 'dating',
    name: 'Dating',
    emoji: '💕',
    description:
      'Optimize your dating profile photos to get more matches and meaningful connections.',
    traits: ['Attractiveness', 'Trustworthiness', 'Likability'],
    platforms: [
      { id: 'tinder', name: 'Tinder' },
      { id: 'hinge', name: 'Hinge' },
      { id: 'bumble', name: 'Bumble' },
      { id: 'other', name: 'Other' },
    ],
    resultLabel: 'matches per week',
    missingPhotos: [
      {
        type: 'full-body',
        description: 'A full-body shot showing your build and style',
        impact: 'Profiles with full-body shots get up to 200% more matches',
      },
      {
        type: 'activity',
        description: 'A photo of you doing something you enjoy',
        impact: 'Activity photos spark more conversations and first messages',
      },
      {
        type: 'social-proof',
        description: 'A photo with friends (where you stand out)',
        impact: 'Social photos signal likability and trustworthiness',
      },
      {
        type: 'candid',
        description: 'A natural, unposed shot that shows the real you',
        impact: 'Candid shots feel more authentic and approachable',
      },
      {
        type: 'headshot',
        description: 'A clear, well-lit close-up of your face',
        impact: 'A strong headshot is the #1 factor in whether someone swipes right',
      },
    ],
  },
  {
    id: 'professional',
    name: 'LinkedIn',
    emoji: '💼',
    description:
      'Present yourself as competent and approachable on LinkedIn and other professional surfaces.',
    traits: ['Competence', 'Influence', 'Approachability'],
    platforms: [
      { id: 'linkedin', name: 'LinkedIn' },
      { id: 'company-website', name: 'Company Website' },
      { id: 'resume', name: 'Resume' },
      { id: 'other', name: 'Other' },
    ],
    resultLabel: 'profile views per week',
    missingPhotos: [
      {
        type: 'current-headshot',
        description: 'A recent, high-quality LinkedIn headshot',
        impact: 'LinkedIn profiles with professional headshots get 14x more views',
      },
      {
        type: 'banner',
        description: 'A custom LinkedIn banner or cover image',
        impact: 'Custom banners make profiles look polished and intentional',
      },
      {
        type: 'team-photo',
        description: 'A photo of you with your team or at work',
        impact: 'Team photos demonstrate leadership and collaboration skills',
      },
      {
        type: 'speaking-event',
        description: 'A photo of you speaking or at a professional event',
        impact: 'Event photos signal expertise and thought leadership',
      },
    ],
  },
  {
    id: 'social',
    name: 'Social Media',
    emoji: '📱',
    description:
      'Build an engaging social media presence that grows your audience.',
    traits: ['Fun', 'Confidence', 'Authenticity'],
    platforms: [
      { id: 'instagram', name: 'Instagram' },
      { id: 'tiktok', name: 'TikTok' },
      { id: 'twitter', name: 'Twitter/X' },
      { id: 'other', name: 'Other' },
    ],
    resultLabel: 'engagement rate',
    missingPhotos: [
      {
        type: 'outdoor',
        description: 'An outdoor photo with natural scenery or urban backdrop',
        impact: 'Outdoor shots get 40% more engagement than indoor photos',
      },
      {
        type: 'candid',
        description: 'A natural, in-the-moment candid shot',
        impact: 'Candid photos feel more relatable and shareable',
      },
      {
        type: 'activity',
        description: 'A photo doing something interesting or creative',
        impact: 'Activity shots tell a story and drive more saves and shares',
      },
      {
        type: 'variety',
        description: 'A mix of close-ups, mid-shots, and wide angles',
        impact: 'Visual variety keeps your feed interesting and boosts follows',
      },
      {
        type: 'golden-hour',
        description: 'A photo taken during golden hour (sunrise or sunset)',
        impact: 'Golden hour lighting dramatically improves photo quality',
      },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    emoji: '🏢',
    description:
      'Build trust and attract customers with professional business photos.',
    traits: ['Trust', 'Clarity', 'Professionalism'],
    platforms: [
      { id: 'google-business', name: 'Google Business' },
      { id: 'yelp', name: 'Yelp' },
      { id: 'website', name: 'Website' },
      { id: 'zillow', name: 'Zillow' },
      { id: 'other', name: 'Other' },
    ],
    resultLabel: 'inquiries per week',
    missingPhotos: [
      {
        type: 'storefront',
        description: 'A clear, inviting photo of your storefront or office',
        impact: 'Storefront photos help customers feel confident visiting',
      },
      {
        type: 'team',
        description: 'A professional team photo showing your people',
        impact: 'Team photos humanize your brand and build trust',
      },
      {
        type: 'product',
        description: 'High-quality photos of your product or service in action',
        impact: 'Product photos are the #1 driver of purchase decisions',
      },
      {
        type: 'action-shot',
        description: 'A photo of your team working or serving customers',
        impact: 'Action shots demonstrate expertise and professionalism',
      },
      {
        type: 'interior',
        description: 'Clean, well-lit photos of your interior space',
        impact: 'Interior photos set expectations and attract walk-ins',
      },
    ],
  },
];

export const ENTRY_CONTEXTS = CONTEXTS.filter(
  context => context.id === 'dating' || context.id === 'professional'
);

/** Lookup a context config by ID. */
export function getContext(id: string): ContextConfig | undefined {
  return CONTEXTS.find(c => c.id === id);
}
